import type { QuickFillItem } from '../types';
import type {
  CellUpdateResult,
  CollectedQuickFillUpdates,
  PreparedElementUpdate,
  PreparedProjectTypeUpdate,
  PropertyMapping,
  QuickFillBindingScope,
  SaveChangesOptions,
} from './quickFillRevitTypes';

const ProjectOnlyQuickFillError =
  'No writable project instance/type parameter found; Quick Fill does not edit family documents';

export function normalizeComparableValue(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim();
}

export function dedupeProjectTypeUpdates(
  updates: PreparedProjectTypeUpdate[]
): PreparedProjectTypeUpdate[] {
  const map = new Map<string, PreparedProjectTypeUpdate>();
  for (const update of updates) {
    const targetKey = (update.TargetElementIds ?? []).map((id) => id.trim()).filter(Boolean).sort().join(',');
    const identityKey = update.FamilyTypeId?.trim() || update.FamilyType;
    const key = `${identityKey}||${update.ParameterName}||${update.BindingScope}||${targetKey}`.toLowerCase();
    const existing = map.get(key);
    if (!existing) {
      map.set(key, update);
      continue;
    }
    const existingValue = normalizeComparableValue(existing.Value);
    const nextValue = normalizeComparableValue(update.Value);
    if (existingValue === nextValue || (existingValue !== '' && nextValue === '')) continue;
    map.set(key, update);
  }
  return Array.from(map.values());
}

export function dedupeElementUpdates(updates: PreparedElementUpdate[]): PreparedElementUpdate[] {
  const map = new Map<string, PreparedElementUpdate>();
  for (const update of updates) {
    const key = `${update.elementId}||${update.parameterName}`.toLowerCase();
    const existing = map.get(key);
    if (!existing) {
      map.set(key, update);
      continue;
    }
    const existingValue = normalizeComparableValue(existing.value);
    const nextValue = normalizeComparableValue(update.value);
    if (existingValue === nextValue || (existingValue !== '' && nextValue === '')) continue;
    map.set(key, update);
  }
  return Array.from(map.values());
}

const getRowMode = (item: QuickFillItem | undefined): 'familyType' | 'instance' =>
  (item as any)?.__rowMode === 'instance' ? 'instance' : 'familyType';

const getFamilyTypeKey = (item: QuickFillItem): string =>
  typeof (item as any).__familyTypeKey === 'string' && (item as any).__familyTypeKey.trim() !== ''
    ? (item as any).__familyTypeKey.trim()
    : item.Name;

const getFamilyTypeId = (item: QuickFillItem): string | undefined => {
  const id = (item as any).__familyTypeId;
  return typeof id === 'string' && id.trim() !== '' ? id.trim() : undefined;
};

const getElementId = (item: QuickFillItem): string | null => {
  const id = (item as any).__elementId;
  return typeof id === 'string' && id.trim() !== '' ? id.trim() : null;
};

const getBindingScope = (item: QuickFillItem, field: string): QuickFillBindingScope | 'unknown' =>
  ((((item as any).__bindingScopeFlags as Record<string, string> | undefined) || {})[field] as
    QuickFillBindingScope | 'unknown' | undefined) ?? 'unknown';

const hasFlag = (item: QuickFillItem, mapName: string, field: string): boolean =>
  ((((item as any)[mapName] as Record<string, boolean> | undefined) || {})[field] === true);

const forceInstanceWrites = (item: QuickFillItem): boolean =>
  (item as any).__forceInstanceParameterWrites === true ||
  (item as any).__quickFillSnapshotShape === 'instanceOnly';

const emptyUpdates = (): CollectedQuickFillUpdates => ({
  projectTypeUpdates: [],
  elementUpdates: [],
  skippedResults: [],
});

function skippedResult(
  item: QuickFillItem,
  revitParameter: string,
  value: unknown
): CellUpdateResult {
  return {
    familyType: item.Name,
    parameter: revitParameter,
    value: String(value ?? ''),
    success: false,
    error: ProjectOnlyQuickFillError,
  };
}

const projectUpdate = (
  item: QuickFillItem,
  parameterName: string,
  value: string,
  bindingScope: QuickFillBindingScope | 'unknown'
): CollectedQuickFillUpdates => ({
  projectTypeUpdates: [{
    FamilyType: getFamilyTypeKey(item),
    ParameterName: parameterName,
    Value: value,
    FamilyTypeId: getFamilyTypeId(item),
    BindingScope: bindingScope,
    rowLabel: item.Name,
  }],
  elementUpdates: [],
  skippedResults: [],
});

const elementUpdate = (
  item: QuickFillItem,
  elementId: string,
  parameterName: string,
  value: string
): CollectedQuickFillUpdates => ({
  projectTypeUpdates: [],
  elementUpdates: [{ elementId, parameterName, value, rowLabel: item.Name }],
  skippedResults: [],
});

export function collectUpdateForCell(
  item: QuickFillItem,
  field: string,
  value: string,
  revitParameter: string,
  configuredBindingScope?: QuickFillBindingScope
): CollectedQuickFillUpdates {
  if (field === 'Name' || !revitParameter) return emptyUpdates();
  const readonlyFlags = ((item as any).__readonlyFlags as Record<string, boolean> | undefined) || {};
  if (readonlyFlags[field] === true) return emptyUpdates();

  const rowMode = getRowMode(item);
  const bindingScope = configuredBindingScope ?? getBindingScope(item, field);
  const elementId = getElementId(item);
  const instanceHasParam = hasFlag(item, '__instanceHasParamFlags', field);
  const typeHasParam = hasFlag(item, '__typeHasParamFlags', field);
  const updateValue = String(value ?? '');

  if (configuredBindingScope === 'type') {
    return projectUpdate(item, revitParameter, updateValue, 'type');
  }
  if (rowMode === 'familyType' && bindingScope === 'instance') {
    return projectUpdate(item, revitParameter, updateValue, 'instance');
  }
  if (rowMode === 'instance' && forceInstanceWrites(item)) {
    return elementId
      ? elementUpdate(item, elementId, revitParameter, updateValue)
      : { ...emptyUpdates(), skippedResults: [skippedResult(item, revitParameter, updateValue)] };
  }
  if (rowMode === 'instance' && elementId && instanceHasParam) {
    return elementUpdate(item, elementId, revitParameter, updateValue);
  }
  if (rowMode === 'instance' && configuredBindingScope !== 'instance' && typeHasParam) {
    return projectUpdate(item, revitParameter, updateValue, 'type');
  }
  if (rowMode === 'instance' && bindingScope === 'instance') {
    return elementId
      ? elementUpdate(item, elementId, revitParameter, updateValue)
      : { ...emptyUpdates(), skippedResults: [skippedResult(item, revitParameter, updateValue)] };
  }
  return projectUpdate(item, revitParameter, updateValue, bindingScope);
}

export function collectUpdatesForRow(
  item: QuickFillItem,
  properties: PropertyMapping[],
  currentItem?: QuickFillItem,
  originalItem?: QuickFillItem,
  options: SaveChangesOptions = {}
): CollectedQuickFillUpdates {
  const collected = emptyUpdates();
  const sourceItem = currentItem ?? item;
  for (const property of properties) {
    if (property.label === 'Name' || !property.revitParameter) continue;
    const currentValue = normalizeComparableValue(sourceItem[property.label as keyof QuickFillItem]);
    if (currentValue === '' && !options.includeEmptyValues) continue;
    if (originalItem && currentValue === normalizeComparableValue(
      originalItem[property.label as keyof QuickFillItem]
    )) continue;

    const update = collectUpdateForCell(
      sourceItem,
      property.label,
      currentValue,
      property.revitParameter,
      property.bindingScope
    );
    collected.projectTypeUpdates.push(...update.projectTypeUpdates);
    collected.elementUpdates.push(...update.elementUpdates);
    collected.skippedResults.push(...update.skippedResults);
  }
  return collected;
}
